import { useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Feld, type FeldOption, type FeldWert } from './Feld';
import './muster.css';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * FILTERLEISTE (Auftrag 17.09.2026, Abschnitt 11)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *   „Bei wenigen Filtern: Inline / Toolbar. Bei umfangreichen Filtern:
 *    rechte Filter-Sidebar oder Drawer. Filterzustand möglichst
 *    URL-kompatibel bzw. navigationsstabil halten."
 *
 * Die Leiste zeigt die ersten `inLeiste` Filter unmittelbar (Vorgabe 3) und
 * schiebt die übrigen hinter den Knopf `[Weitere Filter (n)]`. Die Grenze
 * ist eine Einstellung und keine Entscheidung dieses Bausteins - die Seiten
 * sind zu verschieden.
 *
 * WOHIN DIE ÜBRIGEN FILTER GEHEN
 * ------------------------------
 * Gibt die Seite `beiSchubfach` an, öffnet der Knopf IHR `FilterDrawer` -
 * das ist der vom Auftrag gemeinte Weg bei umfangreichen Filtern. Ohne
 * `beiSchubfach` klappt die Leiste die restlichen Felder unter sich auf;
 * so ist eine Leiste mit fünf Filtern auch ohne Schubfach vollständig
 * bedienbar. `FilterBar` importiert `FilterDrawer` NICHT - sonst hätten die
 * beiden Dateien einen Ringimport, und `FilterDrawer` braucht die
 * Begriffe von hier.
 *
 * FILTERZUSTAND IN DER ADRESSZEILE
 * --------------------------------
 * `useFilterInUrl` legt den Zustand in die Suchparameter. Das ist in dieser
 * Anwendung kein Luxus: Eine gefilterte Bauteilliste wird an den
 * Fachplaner geschickt („schau dir die 14 offenen in E1 an"), und der
 * Rückweg über den Browserknopf muss zum selben Bild führen (Abschnitt 33).
 * `Koordination.tsx` macht das heute schon von Hand - dieses Muster
 * verallgemeinert es.
 *
 * Fremde Parameter (`seite`, `tab`, …) bleiben beim Schreiben unangetastet.
 */

export type FilterWert = string | string[] | boolean | null;
export type FilterWerte = Record<string, FilterWert>;

export type FilterArt = 'auswahl' | 'mehrfachauswahl' | 'text' | 'haken' | 'datum';

export interface FilterDefinition {
  schluessel: string;
  beschriftung: string;
  art: FilterArt;
  optionen?: FeldOption[];
  platzhalter?: string;
  /** Kurze Erklärung im Schubfach. */
  hinweis?: string;
}

/* ═══════════════════════════════════════════════════════════════════════════
   ADRESSZEILE
   ═══════════════════════════════════════════════════════════════════════════ */

/** Leerer Wert je Art - die Vorgabe und zugleich das „nicht gesetzt". */
export function leererWert(art: FilterArt): FilterWert {
  if (art === 'mehrfachauswahl') return [];
  if (art === 'haken') return false;
  return '';
}

/** Ein Filter schränkt ein, wenn er nicht sein leerer Wert ist. */
export function istGesetzt(wert: FilterWert): boolean {
  if (Array.isArray(wert)) return wert.length > 0;
  if (typeof wert === 'boolean') return wert;
  return wert !== null && wert !== '';
}

export function aktiveFilterZahl(werte: FilterWerte): number {
  return Object.values(werte).filter(istGesetzt).length;
}

export function leseFilter(
  parameter: URLSearchParams,
  filter: FilterDefinition[],
  praefix = '',
): FilterWerte {
  const werte: FilterWerte = {};
  for (const f of filter) {
    const roh = parameter.get(praefix + f.schluessel);
    if (roh === null || roh === '') {
      werte[f.schluessel] = leererWert(f.art);
    } else if (f.art === 'mehrfachauswahl') {
      werte[f.schluessel] = roh.split(',').filter((t) => t !== '');
    } else if (f.art === 'haken') {
      werte[f.schluessel] = roh === '1' || roh === 'true';
    } else {
      werte[f.schluessel] = roh;
    }
  }
  return werte;
}

export function schreibeFilter(
  parameter: URLSearchParams,
  werte: FilterWerte,
  filter: FilterDefinition[],
  praefix = '',
): URLSearchParams {
  const neu = new URLSearchParams(parameter);
  for (const f of filter) {
    const name = praefix + f.schluessel;
    const wert = werte[f.schluessel];
    if (!istGesetzt(wert)) { neu.delete(name); continue; }
    if (Array.isArray(wert)) neu.set(name, wert.join(','));
    else if (typeof wert === 'boolean') neu.set(name, '1');
    else neu.set(name, String(wert));
  }
  return neu;
}

/**
 * Filterzustand in der Adresszeile führen. Setzt `replace`, damit Filtern
 * die Browserhistorie nicht mit einem Eintrag je Tastendruck füllt.
 *
 * Nur innerhalb eines Routers benutzbar - in Tests also mit
 * `<MemoryRouter>` umgeben.
 */
export function useFilterInUrl(
  filter: FilterDefinition[],
  praefix = '',
): [FilterWerte, (werte: FilterWerte) => void] {
  const [parameter, setzeParameter] = useSearchParams();

  const werte = useMemo(
    () => leseFilter(parameter, filter, praefix),
    [parameter, filter, praefix],
  );

  const setzen = useCallback((neu: FilterWerte) => {
    setzeParameter(schreibeFilter(parameter, neu, filter, praefix), { replace: true });
  }, [parameter, setzeParameter, filter, praefix]);

  return [werte, setzen];
}

/* ═══════════════════════════════════════════════════════════════════════════
   EIN FILTERFELD - von Leiste und Schubfach gemeinsam benutzt
   ═══════════════════════════════════════════════════════════════════════════ */

export function FilterFeld({
  definition, werte, beiAenderung, breite = 'drittel',
}: {
  definition: FilterDefinition;
  werte: FilterWerte;
  beiAenderung: (werte: FilterWerte) => void;
  breite?: 'voll' | 'halb' | 'drittel';
}) {
  const setzen = (wert: FeldWert) => beiAenderung({
    ...werte,
    [definition.schluessel]: wert as FilterWert,
  });

  return (
    <Feld
      art={definition.art}
      beschriftung={definition.beschriftung}
      optionen={definition.optionen}
      platzhalter={definition.platzhalter
        ?? (definition.art === 'auswahl' ? 'alle' : undefined)}
      hinweis={definition.hinweis}
      wert={werte[definition.schluessel] ?? leererWert(definition.art)}
      beiAenderung={setzen}
      breite={breite}
      className="m-filterleiste-feld"
    />
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   DIE LEISTE
   ═══════════════════════════════════════════════════════════════════════════ */

export interface FilterBarProps {
  filter: FilterDefinition[];
  werte: FilterWerte;
  beiAenderung: (werte: FilterWerte) => void;
  /** Wieviele Filter unmittelbar in der Leiste stehen. Vorgabe 3. */
  inLeiste?: number;
  /** Freitextsuche links in der Leiste. */
  suche?: string;
  beiSuche?: (begriff: string) => void;
  suchePlatzhalter?: string;
  /** Wird statt des eingebauten Schubfachs geöffnet. */
  beiSchubfach?: () => void;
  /** Weitere Bedienelemente ganz rechts. */
  children?: ReactNode;
  className?: string;
}

export function FilterBar({
  filter, werte, beiAenderung, inLeiste = 3,
  suche, beiSuche, suchePlatzhalter,
  beiSchubfach, children, className,
}: FilterBarProps) {
  const [eigenesSchubfach, setEigenesSchubfach] = useState(false);
  const vorne = filter.slice(0, inLeiste);
  const hinten = filter.slice(inLeiste);
  const gesetzt = aktiveFilterZahl(werte);

  const zuruecksetzen = () => {
    const leer: FilterWerte = {};
    for (const f of filter) leer[f.schluessel] = leererWert(f.art);
    beiAenderung(leer);
  };

  return (
    <>
      <div
        className={`m-filterleiste${className ? ` ${className}` : ''}`}
        role="search"
      >
        {beiSuche && (
          <label className="m-filterleiste-suche">
            <span className="m-nur-vorlesen">Suchen</span>
            <input
              type="search"
              value={suche ?? ''}
              placeholder={suchePlatzhalter ?? 'Suchen …'}
              onChange={(e) => beiSuche(e.target.value)}
            />
          </label>
        )}

        {vorne.map((f) => (
          <FilterFeld
            key={f.schluessel}
            definition={f}
            werte={werte}
            beiAenderung={beiAenderung}
          />
        ))}

        {hinten.length > 0 && (
          <button
            type="button"
            className="m-knopf m-knopf-still m-filter-mehr"
            aria-expanded={beiSchubfach ? undefined : eigenesSchubfach}
            aria-controls={beiSchubfach ? undefined : 'm-filter-mehrflaeche'}
            onClick={() => (beiSchubfach
              ? beiSchubfach()
              : setEigenesSchubfach((o) => !o))}
          >
            Weitere Filter
            <span className="m-filter-mehr-zahl">({hinten.length})</span>
          </button>
        )}

        {gesetzt > 0 && (
          <span className="m-filter-stand">
            <span className="m-filter-zahl">
              {gesetzt === 1 ? '1 Filter gesetzt' : `${gesetzt} Filter gesetzt`}
            </span>
            <button
              type="button"
              className="m-knopf m-knopf-still m-filter-zuruecksetzen"
              onClick={zuruecksetzen}
            >
              Zurücksetzen
            </button>
          </span>
        )}

        {children}
      </div>

      {eigenesSchubfach && hinten.length > 0 && (
        <div className="m-filter-mehrflaeche" id="m-filter-mehrflaeche">
          {hinten.map((f) => (
            <FilterFeld
              key={f.schluessel}
              definition={f}
              werte={werte}
              beiAenderung={beiAenderung}
            />
          ))}
        </div>
      )}
    </>
  );
}
